// src/contexts/AlertContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';

const AlertContext = createContext();

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  const addAlert = useCallback((alert) => {
    const id = Date.now() + Math.random();
    const newAlert = {
      id,
      type: 'info', // Default type
      title: '',
      message: '',
      duration: 5000, // Default 5 seconds
      closable: true,
      actions: [],
      ...alert
    };

    setAlerts(prev => [...prev, newAlert]);

    // Auto remove alert after duration (unless it's a confirm type)
    if (newAlert.duration > 0 && newAlert.type !== 'confirm') {
      setTimeout(() => {
        removeAlert(id);
      }, newAlert.duration);
    }

    return id;
  }, []);

  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  }, []);

  const clearAllAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // Convenience methods for different alert types
  const success = useCallback((message, options = {}) => {
    return addAlert({
      type: 'success',
      message,
      ...options
    });
  }, [addAlert]);

  const error = useCallback((message, options = {}) => {
    return addAlert({
      type: 'error',
      message,
      duration: 7000, // Error messages stay longer
      ...options
    });
  }, [addAlert]);

  const warning = useCallback((message, options = {}) => {
    return addAlert({
      type: 'warning',
      message,
      duration: 6000,
      ...options
    });
  }, [addAlert]);

  const info = useCallback((message, options = {}) => {
    return addAlert({
      type: 'info',
      message,
      ...options
    });
  }, [addAlert]);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      const alertId = addAlert({
        type: 'confirm',
        message,
        duration: 0, // No auto-remove for confirm dialogs
        actions: [
          {
            label: options.cancelText || 'Cancel',
            type: 'secondary',
            onClick: () => {
              removeAlert(alertId);
              resolve(false);
            }
          },
          {
            label: options.confirmText || 'Confirm',
            type: 'primary',
            onClick: () => {
              removeAlert(alertId);
              resolve(true);
            }
          }
        ],
        ...options
      });
    });
  }, [addAlert, removeAlert]);

  const value = {
    alerts,
    addAlert,
    removeAlert,
    clearAllAlerts,
    success,
    error,
    warning,
    info,
    confirm
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};